import { requestDataUtil, sendGAEvent } from '../utils';

export enum RegistrationEventEnum {
  registration_form_appearance = 'registration_form_appearance',
  registration_form_first_touched = 'registration_form_first_touched',
  registration_google_auth_button_click = 'registration_google_auth_button_click',
  registration_form_submit = 'registration_form_submit',
  registration_form_close = 'registration_form_close',
}

export type TRegistrationType =
  | 'landing'
  | 'lobby'
  | 'modal-lobby'
  | 'modal-landing';

export interface IRegistrationEventData {
  type?: TRegistrationType;
  error_data?: any;
  request_data?: Record<string, unknown>;
  status: 'success' | 'failed';
  action: 'login' | 'registration';
  method: 'email' | 'google';
}

/**
 *
 * @returns {string} - The event location based on the current URL.
 * @description This function determines the event location based on the current URL.
 * It checks the pathname and search parameters to determine if the event is in a modal or a specific landing page.
 */
export const getEventLocation = (): string => {
  const { pathname, search } = window.location;
  const variant = search.includes('modal=registration') ? 'modal-' : '';
  const place = pathname.includes('/promo/') ? 'landing' : 'lobby';
  return `${variant}${place}`;
};

/**
 *
 * @param { TRegistrationType } type  - The type of registration form appearance.
 * @description This function sends a Google Analytics event when the registration form appears.
 * It uses the `gtag` function to send the event with the specified type.
 */
export const sendRegFormAppearanceEvent = (type?: TRegistrationType) => {
  sendGAEvent(RegistrationEventEnum.registration_form_appearance, {
    event_category: 'registration',
    type: type || getEventLocation(),
  });
};

/**
 *
 * @param type - The type of registration form appearance.
 * @description This function sends a Google Analytics event when the registration form is first touched.
 * It uses the `gtag` function to send the event with the specified type.
 */
export const sendRegFormFirstTouchedEvent = (type?: TRegistrationType) => {
  sendGAEvent(RegistrationEventEnum.registration_form_first_touched, {
    event_category: 'registration',
    type: type || getEventLocation(),
  });
};

/**
 *
 * @param type - The type of registration form appearance.
 * @description This function sends a Google Analytics event when the registration form is submitted.
 * It uses the `gtag` function to send the event with the specified type.
 */
export const sendRegGoogleAuthButtonClickEvent = (type?: TRegistrationType) => {
  sendGAEvent(RegistrationEventEnum.registration_google_auth_button_click, {
    event_category: 'registration',
    type: type || getEventLocation(),
  });
};

/**
 *
 * @param {IRegistrationEvent} data - The data object containing registration event details.
 * @description This function sends a Google Analytics event when the registration form is submitted.
 * It uses the `gtag` function to send the event with the specified data.
 * The data object should contain the following properties:
 */
export const sendRegFormSubmitEvent = (data: IRegistrationEventData) => {
  try {
    sendGAEvent(RegistrationEventEnum.registration_form_submit, {
      event_category: 'registration',
      ...data,
      type: data.type || getEventLocation(),
      request_data:
        data.request_data &&
        JSON.stringify(
          requestDataUtil(data.request_data, data.error_data) || ''
        ),
      error_data: data.error_data && JSON.stringify(data.error_data),
    });
    return;
  } catch (e) {
    console.error('Error sending registration form submit event:', e);
    return;
  }
};

/**
 *
 * @param type - The type of registration form close (click to close button).
 * @description This function sends a Google Analytics event when the registration form is closed.
 * It uses the `gtag` function to send the event with the specified type.
 */
export const sendRegFormCloseEvent = (type?: TRegistrationType) => {
  sendGAEvent(RegistrationEventEnum.registration_form_close, {
    event_category: 'registration',
    type: type || getEventLocation(),
  });
};
